import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import { WeightInput } from 'ui-components/components/weight-input';
import { noop } from 'ui-components/default-props/noop';

import { INPUT_KEYS, LOCATION_OPTIONS, PICKUP_LOCATION_VALUE_REQUIRES_SPECIALINSTRUCTIONS } from '../constants';
import messages from '../messages';
import style from './style.module.less';

const PickupDetailsSection = ({ onChange = noop, weight = 0, pickupLocation = '', instructions = '', isDisabled = false }) => {
  const labelClassName = isDisabled ? style['static-label'] : style.label;
  const selectedOption = LOCATION_OPTIONS.find((o) => o.apiValue === pickupLocation)?.displayName;
  const togglerDisplay = selectedOption ? (
    <FormattedMessage {...selectedOption} />
  ) : (
    <FormattedMessage {...messages.pickupLocationDropdownPlaceholder} />
  );
  const { formatMessage } = useIntl();
  const onClick = useMemo(() => onChange(INPUT_KEYS.PICKUP_LOCATION), [onChange]);

  const renderWeight = () => {
    if (isDisabled) {
      return (
        <div className={labelClassName}>
          <span className={style.static}>{weight}</span>
        </div>
      );
    }
    return (
      <div className={style['weight-input']}>
        <WeightInput onChange={onChange(INPUT_KEYS.TOTAL_WEIGHT)} weight={weight} />
      </div>
    );
  };

  const renderLocation = () => {
    if (isDisabled) {
      return <span className={style.static}>{togglerDisplay}</span>;
    }
    return (
      <DropdownMenu fullWidth id={INPUT_KEYS.PICKUP_LOCATION} variant='form'>
        <DropdownMenuToggler className={style.toggler} title={togglerDisplay}>
          {togglerDisplay || formatMessage(messages.pickupLocationDropdownPlaceholder)}
        </DropdownMenuToggler>
        {LOCATION_OPTIONS.map((child) => (
          <DropdownMenuItem key={child.apiValue} onClick={() => onClick(child.apiValue)}>
            {formatMessage(child.displayName)}
          </DropdownMenuItem>
        ))}
      </DropdownMenu>
    );
  };

  const renderInstructions = () => {
    if (isDisabled) {
      return <p className={style.static}>{instructions}</p>;
    }

    return (
      <FloatTextArea
        hasError={pickupLocation === PICKUP_LOCATION_VALUE_REQUIRES_SPECIALINSTRUCTIONS && !instructions}
        maxLength={200}
        onChange={onChange(INPUT_KEYS.SPECIAL_INSTRUCTION)}
        value={instructions}
      />
    );
  };

  return (
    <>
      <div className={labelClassName}>
        <FormattedMessage {...messages.totalWeightPickupLabel} />
      </div>
      {renderWeight()}
      <div className={labelClassName}>
        <FormattedMessage {...messages.pickupLocationPickupLabel} />
      </div>
      <div>{renderLocation()}</div>
      <div className={labelClassName}>
        <FormattedMessage {...messages.specialInstructionPickupLabel} />
      </div>
      <div>{renderInstructions()}</div>
    </>
  );
};

PickupDetailsSection.propTypes = {
  onChange: PropTypes.func,
  weight: PropTypes.number,
  pickupLocation: PropTypes.string,
  instructions: PropTypes.string,
  isDisabled: PropTypes.bool,
};

export default PickupDetailsSection;
